// ONE way to place a call from anywhere in the ERP: through the agent's own
// connected phone (Daksha Caller). Never `tel:` — that opens Windows Phone
// Link on desktops, which the team does not use.
//
// The backend enforces permission, assignment, DND, cooldown and the atomic
// reservation; the phone dials within a second of the click.
//
// Every step is announced on the `daksha:dial` window event so the global
// DialTracker card can show it live (sent → received → dialling → on call →
// ended, or the exact reason it failed). Callers keep the same promise-based
// result as before.
import { describePhone, dialProgress } from './phoneHealth';

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

export const DIAL_EVENT = 'daksha:dial';
const emit = (detail) => {
    try { window.dispatchEvent(new CustomEvent(DIAL_EVENT, { detail })); } catch { /* no window (tests) */ }
};
// When the DialTracker card is on screen it says everything; the toasts are
// only the fallback for a page that does not mount it.
const trackerShown = () => { try { return !!window.__dakshaDialTracker; } catch { return false; } };

const toast = (text, ok = true) => {
    if (trackerShown()) return;
    try {
        const el = document.createElement('div');
        el.textContent = text;
        el.style.cssText = 'position:fixed;bottom:88px;right:24px;z-index:9999;padding:10px 16px;border-radius:12px;'
            + `background:${ok ? '#16A34A' : '#D97706'};color:#fff;font:600 13px Inter,system-ui,sans-serif;`
            + 'box-shadow:0 8px 24px rgba(0,0,0,.18);transition:opacity .4s;';
        document.body.appendChild(el);
        setTimeout(() => { el.style.opacity = '0'; }, 2600);
        setTimeout(() => el.remove(), 3100);
    } catch { /* toast is garnish */ }
};

const ACK_WAIT_MS = 8000;

async function firebase() {
    const [{ ref, onValue, get }, { db, auth }] = await Promise.all([
        import('firebase/database'),
        import('../../integrations/firebase'),
    ]);
    return { ref, onValue, get, db, auth };
}

/**
 * Read the agent's phone presence once, judged on SERVER time.
 * Null when it cannot be read — the server still gates the call.
 */
async function readPhoneView() {
    try {
        const { ref, get, db, auth } = await firebase();
        const uid = auth.currentUser?.uid;
        if (!uid) return null;
        const [p, off] = await Promise.all([
            get(ref(db, `callappPresence/${uid}`)),
            get(ref(db, '.info/serverTimeOffset')).catch(() => null),
        ]);
        return describePhone(p.val() || null, Date.now() + (Number(off?.val()) || 0));
    } catch { return null; }
}

/**
 * Wait for the handset to confirm the dial command.
 *
 * Confirmation is either the phone's own report at `tc/commands/{uid}/{cmdId}/phone`
 * (v3.11+, written straight to RTDB the instant the command arrives) or the server's
 * `status: acked`. Resolves { acked, failed, reason }.
 */
async function waitForAck(cmdId, timeoutMs) {
    try {
        const { ref, onValue, db, auth } = await firebase();
        const uid = auth.currentUser?.uid;
        if (!uid || !cmdId) return { acked: false };
        const node = ref(db, `tc/commands/${uid}/${cmdId}`);
        return await new Promise(resolve => {
            let done = false;
            let unsub = null;
            const finish = (v) => { if (done) return; done = true; clearTimeout(timer); try { unsub?.(); } catch { /* already detached */ } resolve(v); };
            const timer = setTimeout(() => finish({ acked: false }), timeoutMs);
            unsub = onValue(node, snap => {
                const p = dialProgress(snap.val());
                if (p.failed) finish({ acked: p.acked, failed: true, reason: p.reason });
                else if (p.acked) finish({ acked: true });
            }, () => finish({ acked: false }));
            if (done) unsub();   // settled synchronously from cache
        });
    } catch { return { acked: false }; }
}

/** Call off a dial the phone never picked up, freeing the agent's reservation. */
export async function cancelDial(cmdId, { quiet = false } = {}) {
    try {
        const { authHeaders } = await import('../api/authHeaders');
        const r = await fetch(`${PROXY}/api/tc/call/cancel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ cmdId }),
        });
        const j = await r.json().catch(() => ({}));
        if (j.ok) { if (!quiet) toast('Call cancelled — you are free to dial again'); return { cancelled: true }; }
        if (j.acked) { if (!quiet) toast('Your phone picked the call up after all', true); return { cancelled: false, acked: true }; }
        if (!quiet) toast(`⚠ ${j.error || 'Could not cancel the call'}`, false);
        return { cancelled: false, error: j.error };
    } catch {
        if (!quiet) toast('⚠ No network — could not cancel the call', false);
        return { cancelled: false, error: 'No network' };
    }
}

/**
 * Place a call. Always resolves to a result object:
 *   { ok: true,  confirmed: true,  sessionId, cmdId, name }   the phone confirmed it
 *   { ok: true,  unconfirmed: true, cancel(), … }             sent, not yet confirmed
 *   { ok: false, blocked, error }                             no call was placed
 *
 * `blocked` separates the two failures that must be handled DIFFERENTLY:
 *   blocked: true  — the server refused on policy (DND, cooldown, assigned to another
 *                    agent, already on a call). No call happened and none should; never
 *                    offer to record one.
 *   blocked: false — the dial itself did not get through (phone offline, network,
 *                    server error). The agent may well have called from their handset,
 *                    so logging it by hand is legitimate.
 */
export async function dialViaPhone(mobile, purpose = 'manual') {
    const m = String(mobile || '').replace(/\D/g, '').slice(-10);
    const id = `d_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    if (m.length !== 10) {
        toast('No valid number on this record', false);
        emit({ id, phase: 'refused', mobile: m, error: 'No valid 10-digit number on this record' });
        return { ok: false, blocked: true, error: 'No valid 10-digit number on this record' };
    }
    emit({ id, phase: 'checking', mobile: m });

    // Look before sending: a phone that has gone silent will not ring, and the
    // agent should hear why NOW, not after waiting for an acknowledgement.
    const view = await readPhoneView();
    if (view && (view.status === 'offline' || view.status === 'none')) {
        const error = `${view.headline} — ${view.reasons[0]?.text || 'open Daksha Caller on your phone'}`;
        toast(`⚠ ${error}`, false);
        emit({ id, phase: 'offline', mobile: m, view, error });
        return { ok: false, blocked: false, offline: true, error };
    }

    try {
        const { authHeaders } = await import('../api/authHeaders');
        const r = await fetch(`${PROXY}/api/tc/call/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ mobile: m, purpose, deviceId: 'web' }),
        });
        const j = await r.json().catch(() => ({}));
        if (j.ok) {
            const name = j.name || '';
            emit({ id, phase: 'sent', mobile: m, name, cmdId: j.cmdId, sessionId: j.sessionId, expectAck: !!j.expectAck, ackTimeoutMs: Number(j.ackTimeoutMs) || ACK_WAIT_MS, view });
            // The server accepting the command is NOT the same as the phone dialling it.
            if (!j.expectAck) {
                toast(`📲 Sent to your phone — dialing ${name || m}…`);
                return { ok: true, confirmed: false, sessionId: j.sessionId, cmdId: j.cmdId, name };
            }
            toast('Sending to your phone…');
            const ack = await waitForAck(j.cmdId, Number(j.ackTimeoutMs) || ACK_WAIT_MS);
            if (ack.failed) {
                // The phone answered — with a no. Free the reservation it was holding.
                toast(`⚠ ${ack.reason}`, false);
                cancelDial(j.cmdId, { quiet: true });
                return { ok: false, blocked: false, error: ack.reason };
            }
            if (ack.acked) {
                toast(`📲 Ringing ${name || m} on your phone`);
                return { ok: true, confirmed: true, sessionId: j.sessionId, cmdId: j.cmdId, name };
            }
            // Unconfirmed. The call is NOT torn down here — the handset may be a
            // second late, and cancelling a live call would be worse than waiting.
            // The tracker card offers Cancel and clears it once the command can no
            // longer be dialled (the phone refuses commands over a minute old).
            toast('⚠ Your phone has not confirmed this call yet', false);
            return {
                ok: true, confirmed: false, unconfirmed: true,
                sessionId: j.sessionId, cmdId: j.cmdId, name,
                cancel: () => cancelDial(j.cmdId),
            };
        }
        const error = j.error || 'Could not reach your phone — is Daksha Caller signed in?';
        toast(`⚠ ${error}`, false);
        // A CONTACT-policy refusal (403 DND · 409 assigned elsewhere or already on a
        // call · 429 cooldown) answers `ok:false` from the route itself. The capability
        // guard in front of it answers without an `ok` field at all — that means "you
        // may not dial from here", not "this customer must not be called", so it is not
        // treated as blocked and the agent can still record a call they made by hand.
        // A 409 for an offline DEVICE is not a policy refusal either.
        const fromRoute = Object.prototype.hasOwnProperty.call(j, 'ok');
        const deviceDown = j.device === 'offline';
        emit({ id, phase: deviceDown ? 'offline' : 'refused', mobile: m, error, view: deviceDown ? await readPhoneView() : null });
        return { ok: false, blocked: !deviceDown && fromRoute && [403, 409, 429].includes(r.status), offline: deviceDown, error };
    } catch {
        const error = 'No network on this computer — the call was not placed';
        toast(`⚠ ${error}`, false);
        emit({ id, phase: 'refused', mobile: m, error });
        return { ok: false, blocked: false, error };
    }
}

export default dialViaPhone;
